import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import api from '../services/api';
import { LangContext } from '../App';

const T = {
  fr: {
    title: 'Recette',
    retour: '← Retour aux recettes',
    ingredients: 'Ingrédients',
    steps: 'Étapes',
    avis: 'Avis',
    noAvis: 'Aucun avis pour le moment.',
    votreNote: 'Votre rating',
    comment: 'Laissez un comment…',
    soumettre: 'Soumettre un avis',
    favoris: '♡ Sauvegarder',
    retire: '♥ Sauvegardé',
    connexionRequise: 'Vous devez être connecté pour effectuer cette action.',
    prep: 'Prép.',
    cuisson: 'Cuisson',
    min: ' min',
    servings: 'servings',
    editorial: 'Éditorial',
    parAuteur: 'Par',
    nonTrouve: 'Recette introuvable.',
    difficultes: { facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile', expert: 'Expert' },
    especesLabels: {
      orignal: 'Orignal',
      chevreuil: 'Chevreuil',
      caribou: 'Caribou',
      dinde_sauvage: 'Dinde sauvage',
      outarde: 'Outarde',
      canard: 'Canard',
      bernache: 'Bernache',
      brochet: 'Brochet',
      dore: 'Doré',
      achigan: 'Achigan',
      truite: 'Truite',
      saumon: 'Saumon',
      autre: 'Autre',
    },
    envoi: 'Envoi…',
    erreurAvis: 'Erreur lors de la soumission.',
    avisEnvoye: 'Merci pour votre avis !',
  },
  en: {
    title: 'Recipe',
    retour: '← Back to recipes',
    ingredients: 'Ingredients',
    steps: 'Steps',
    avis: 'Reviews',
    noAvis: 'No reviews yet.',
    votreNote: 'Your rating',
    comment: 'Leave a comment…',
    soumettre: 'Submit review',
    favoris: '♡ Save',
    retire: '♥ Saved',
    connexionRequise: 'You must be logged in to perform this action.',
    prep: 'Prep',
    cuisson: 'Cook',
    min: ' min',
    servings: 'servings',
    editorial: 'Editorial',
    parAuteur: 'By',
    nonTrouve: 'Recipe not found.',
    difficultes: { facile: 'Easy', moyen: 'Medium', difficile: 'Hard', expert: 'Expert' },
    especesLabels: {
      orignal: 'Moose',
      chevreuil: 'Deer',
      caribou: 'Caribou',
      dinde_sauvage: 'Wild Turkey',
      outarde: 'Canada Goose',
      canard: 'Duck',
      bernache: 'Brant',
      brochet: 'Pike',
      dore: 'Walleye',
      achigan: 'Bass',
      truite: 'Trout',
      saumon: 'Salmon',
      autre: 'Other',
    },
    envoi: 'Sending…',
    erreurAvis: 'Error submitting review.',
    avisEnvoye: 'Thank you for your review!',
  },
};

const DIFFICULTY_COLOR = {
  facile: '#34d399',
  moyen: '#fbbf24',
  difficile: '#fb923c',
  expert: '#fb7185',
};
const ESPECE_EMOJI = {
  orignal: '🦌',
  chevreuil: '🦌',
  caribou: '🦌',
  dinde_sauvage: '🦃',
  outarde: '🪿',
  canard: '🦆',
  bernache: '🪿',
  brochet: '🐟',
  dore: '🐟',
  achigan: '🐟',
  truite: '🐟',
  saumon: '🐟',
};

function StarRating({ value, onChange, readOnly = false, size = 22 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <span style={{ display: 'inline-flex', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          onClick={() => !readOnly && onChange && onChange(n)}
          onMouseEnter={() => !readOnly && setHovered(n)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          style={{
            fontSize: size,
            cursor: readOnly ? 'default' : 'pointer',
            color: n <= (hovered || value) ? '#fbbf24' : 'rgba(255,255,255,0.2)',
            transition: 'color 0.1s',
            userSelect: 'none',
          }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

export default function DetailRecette() {
  const { id } = useParams();
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const navigate = useNavigate();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [userNote, setUserNote] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [submittingFavorite, setSubmittingFavorite] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchRecipe = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/recipes/${id}`);
        setRecipe(data);
        setReviews(data.avis || []);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!token) {
      setAuthMessage(t.connexionRequise);
      return;
    }
    setSubmittingFavorite(true);
    try {
      await api.post(`/recipes/${id}/favori`);
      setIsFavorite((f) => !f);
    } catch {
      // ignore
    } finally {
      setSubmittingFavorite(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!token) {
      setAuthMessage(t.connexionRequise);
      return;
    }
    if (!userNote) return;
    setSubmittingReview(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      const { data } = await api.post(`/recipes/${id}/avis`, {
        rating: userNote,
        comment: userComment,
      });
      setReviews((prev) => [data, ...prev]);
      setUserNote(0);
      setUserComment('');
      setReviewSuccess(t.avisEnvoye);
    } catch {
      setReviewError(t.erreurAvis);
    } finally {
      setSubmittingReview(false);
    }
  };

  const s = {
    page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
    hero: {
      background: 'linear-gradient(135deg, #0a1f0e 0%, #1f3e1f 100%)',
      padding: '2.5rem 2rem 2rem',
      textAlign: 'center',
    },
    heroEmoji: { fontSize: '3.5rem', marginBottom: '0.75rem' },
    heroTitle: {
      fontFamily: 'Georgia, serif',
      fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
      color: '#fff',
      margin: '0 0 0.75rem',
    },
    badgeRow: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      justifyContent: 'center',
      marginBottom: '1rem',
    },
    badge: (color) => ({
      fontSize: '0.75rem',
      padding: '3px 10px',
      borderRadius: '999px',
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
      fontWeight: 600,
    }),
    timingRow: {
      display: 'flex',
      gap: '20px',
      justifyContent: 'center',
      color: 'rgba(255,255,255,0.65)',
      fontSize: '0.9rem',
      flexWrap: 'wrap',
    },
    timingItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
    timingLabel: {
      fontSize: '0.7rem',
      color: 'rgba(255,255,255,0.4)',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
    timingValue: { color: '#fff', fontWeight: 600 },
    body: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)',
      gap: '2rem',
      padding: '2rem',
      maxWidth: '1100px',
      margin: '0 auto',
    },
    card: {
      background: 'var(--couleur-fond-carte)',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '1.5rem',
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontFamily: 'Georgia, serif',
      fontSize: '1.2rem',
      color: 'var(--couleur-or)',
      margin: '0 0 1rem',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      paddingBottom: '0.5rem',
    },
    ingredientRow: {
      display: 'flex',
      gap: '8px',
      padding: '6px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      fontSize: '0.9rem',
    },
    ingredientNom: { flex: 1, color: '#fff' },
    ingredientQty: { color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' },
    stepRow: { display: 'flex', gap: '14px', marginBottom: '1rem', alignItems: 'flex-start' },
    stepNum: {
      width: 28,
      height: 28,
      borderRadius: '50%',
      background: 'rgba(201,162,39,0.2)',
      border: '1px solid rgba(201,162,39,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      flexShrink: 0,
    },
    stepText: {
      fontSize: '0.95rem',
      lineHeight: 1.7,
      color: 'rgba(255,255,255,0.85)',
      paddingTop: '4px',
    },
    btnFavorite: (active) => ({
      width: '100%',
      padding: '11px',
      borderRadius: '8px',
      border: 'none',
      background: active ? 'rgba(201,162,39,0.25)' : 'rgba(255,255,255,0.08)',
      color: active ? 'var(--couleur-or)' : '#fff',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 600,
      marginBottom: '1.5rem',
      transition: 'all 0.15s',
    }),
    ratingInfo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' },
    ratingBig: { fontSize: '2rem', fontWeight: 700, color: '#fbbf24' },
    ratingCount: { fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)' },
    reviewCard: { padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' },
    reviewAuthor: { fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
    reviewText: {
      fontSize: '0.9rem',
      color: 'rgba(255,255,255,0.8)',
      marginTop: '6px',
      lineHeight: 1.6,
    },
    form: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1rem' },
    textarea: {
      padding: '10px 12px',
      borderRadius: '8px',
      background: 'rgba(255,255,255,0.07)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.9rem',
      resize: 'vertical',
      minHeight: '80px',
      outline: 'none',
    },
    btnSubmit: {
      padding: '10px',
      borderRadius: '8px',
      border: 'none',
      background: 'var(--couleur-vert-accent)',
      color: '#fff',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.9rem',
    },
    authMsg: { color: '#fb923c', fontSize: '0.85rem', marginTop: '6px' },
    backLink: {
      display: 'inline-block',
      color: 'rgba(255,255,255,0.55)',
      fontSize: '0.85rem',
      textDecoration: 'none',
      padding: '1rem 2rem 0',
      transition: 'color 0.15s',
    },
    skeleton: {
      background: 'rgba(255,255,255,0.08)',
      borderRadius: '6px',
      animation: 'pulse 1.5s ease-in-out infinite',
    },
  };

  if (loading) {
    return (
      <div style={s.page}>
        <Header />
        <div style={{ ...s.hero, textAlign: 'left' }}>
          <div style={{ ...s.skeleton, height: '2rem', width: '40%', marginBottom: '1rem' }} />
          <div style={{ ...s.skeleton, height: '1rem', width: '60%' }} />
        </div>
        <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ ...s.skeleton, height: '80px', marginBottom: '1rem' }} />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !recipe) {
    return (
      <div style={s.page}>
        <Header />
        <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🍽️</div>
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>{t.nonTrouve}</p>
          <Link
            to="/recipes"
            style={{ color: 'var(--couleur-or)', textDecoration: 'none', fontSize: '0.9rem' }}
          >
            {t.retour}
          </Link>
        </div>
      </div>
    );
  }

  const avgRating = recipe.average_rating ? parseFloat(recipe.average_rating).toFixed(1) : null;
  const difficultyLabel = t.difficultes[recipe.difficulty] || recipe.difficulty;
  const speciesLabel = t.especesLabels[recipe.main_species] || recipe.main_species;
  const emoji = ESPECE_EMOJI[recipe.main_species] || '🍽️';

  return (
    <div style={s.page}>
      <Header />

      <Link
        to="/recipes"
        style={s.backLink}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--couleur-or)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.55)')}
      >
        {t.retour}
      </Link>

      {/* Hero */}
      <div style={s.hero}>
        <div style={s.heroEmoji}>{emoji}</div>
        <h1 style={s.heroTitle}>{recipe.title}</h1>

        <div style={s.badgeRow}>
          {recipe.main_species && <span style={s.badge('#c9a227')}>{speciesLabel}</span>}
          {recipe.difficulty && (
            <span style={s.badge(DIFFICULTY_COLOR[recipe.difficulty] || '#aaa')}>
              {difficultyLabel}
            </span>
          )}
          {recipe.type === 'editoriale' && <span style={s.badge('#5b8def')}>{t.editorial}</span>}
        </div>

        {recipe.auteur_nom && (
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
            {t.parAuteur} {recipe.auteur_nom}
          </p>
        )}

        <div style={s.timingRow}>
          {recipe.prep_time_min && (
            <div style={s.timingItem}>
              <span style={s.timingLabel}>{t.prep}</span>
              <span style={s.timingValue}>
                {recipe.prep_time_min}
                {t.min}
              </span>
            </div>
          )}
          {recipe.cook_time_min && (
            <div style={s.timingItem}>
              <span style={s.timingLabel}>{t.cuisson}</span>
              <span style={s.timingValue}>
                {recipe.cook_time_min}
                {t.min}
              </span>
            </div>
          )}
          {recipe.servings && (
            <div style={s.timingItem}>
              <span style={s.timingLabel}>{t.servings}</span>
              <span style={s.timingValue}>{recipe.servings}</span>
            </div>
          )}
          {avgRating && (
            <div style={s.timingItem}>
              <span style={s.timingLabel}>{t.avis}</span>
              <span style={s.timingValue}>
                ⭐ {avgRating} ({recipe.reviews_count || reviews.length})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Two-column body */}
      <div style={s.body}>
        {/* Left: ingredients + steps */}
        <div>
          {/* Ingredients */}
          {recipe.ingredients && recipe.ingredients.length > 0 && (
            <div style={s.card}>
              <h2 style={s.sectionTitle}>{t.ingredients}</h2>
              {recipe.ingredients.map((ing, i) => (
                <div key={i} style={s.ingredientRow}>
                  <span style={s.ingredientNom}>{ing.name}</span>
                  <span style={s.ingredientQty}>
                    {ing.quantite}
                    {ing.unite ? ` ${ing.unite}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Steps */}
          {recipe.steps && recipe.steps.length > 0 && (
            <div style={s.card}>
              <h2 style={s.sectionTitle}>{t.steps}</h2>
              {recipe.steps.map((step, i) => (
                <div key={i} style={s.stepRow}>
                  <div style={s.stepNum}>{i + 1}</div>
                  <div style={s.stepText}>{step.texte}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: favorite + reviews */}
        <div>
          {/* Favorite button */}
          <button
            style={s.btnFavorite(isFavorite)}
            onClick={handleToggleFavorite}
            disabled={submittingFavorite}
          >
            {isFavorite ? t.retire : t.favoris}
          </button>

          {/* Reviews section */}
          <div style={s.card}>
            <h2 style={s.sectionTitle}>{t.avis}</h2>

            {avgRating && (
              <div style={s.ratingInfo}>
                <span style={s.ratingBig}>{avgRating}</span>
                <div>
                  <StarRating value={Math.round(parseFloat(avgRating))} readOnly />
                  <div style={s.ratingCount}>({recipe.reviews_count || reviews.length})</div>
                </div>
              </div>
            )}

            {/* Review form */}
            <form onSubmit={handleSubmitReview} style={s.form}>
              <label style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)' }}>
                {t.votreNote}
              </label>
              <StarRating value={userNote} onChange={setUserNote} size={28} />
              <textarea
                style={s.textarea}
                placeholder={t.comment}
                value={userComment}
                onChange={(e) => setUserComment(e.target.value)}
                rows={3}
              />
              {authMessage && <p style={s.authMsg}>{authMessage}</p>}
              {reviewError && (
                <p style={{ color: '#fb7185', fontSize: '0.85rem' }}>{reviewError}</p>
              )}
              {reviewSuccess && (
                <p style={{ color: '#34d399', fontSize: '0.85rem' }}>{reviewSuccess}</p>
              )}
              <button type="submit" style={s.btnSubmit} disabled={submittingReview || !userNote}>
                {submittingReview ? t.envoi : t.soumettre}
              </button>
            </form>

            {/* Review list */}
            <div style={{ marginTop: '1.5rem' }}>
              {reviews.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.88rem' }}>{t.noAvis}</p>
              ) : (
                reviews.slice(0, 20).map((review, i) => (
                  <div key={i} style={s.reviewCard}>
                    <StarRating value={review.rating} readOnly size={16} />
                    {review.comment && <p style={s.reviewText}>{review.comment}</p>}
                    <p style={s.reviewAuthor}>
                      {review.auteur_nom || '—'} &middot;{' '}
                      {review.created_at
                        ? new Date(review.created_at).toLocaleDateString(
                            lang === 'fr' ? 'fr-CA' : 'en-CA'
                          )
                        : ''}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 700px) {
          .detail-body { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
